<template>
  <list-page-shell>
    <template #actions>
      <el-button-group>
        <el-button size="small" @click="setPreset('week')">{{ $t('common.thisWeek') }}</el-button>
        <el-button size="small" @click="setPreset('month')">{{ $t('common.thisMonth') }}</el-button>
        <el-button size="small" @click="setPreset('year')">{{ $t('common.thisYear') }}</el-button>
      </el-button-group>
      <el-button size="small" @click="$router.push('/finance/lead-cost')">{{ $t('financeCenterPage.leadCostNav') }}</el-button>
      <el-button size="small" icon="el-icon-refresh" @click="load">{{ $t('common.refresh') }}</el-button>
    </template>

    <template #filters>
      <el-form inline size="small">
        <el-form-item :label="$t('common.date')">
          <el-date-picker
            v-model="range"
            type="daterange"
            value-format="yyyy-MM-dd"
            :start-placeholder="$t('common.dateRangeStart')"
            :end-placeholder="$t('common.dateRangeEnd')"
            style="width: 320px;"
          />
        </el-form-item>
      </el-form>
    </template>

    <div class="finance-overview">
      <div class="metric-card">
        <span class="metric-card__label">{{ $t('financeCenterPage.overviewTotalContract') }}</span>
        <strong class="metric-card__value">{{ formatUsd(finance.totalSalesAmount) }}</strong>
      </div>
      <div class="metric-card">
        <span class="metric-card__label">{{ $t('projectPage.totalReceived') }}</span>
        <strong class="metric-card__value">{{ formatUsd(finance.totalReceived) }}</strong>
      </div>
      <div class="metric-card">
        <span class="metric-card__label">{{ $t('projectPage.totalBalance') }}</span>
        <strong class="metric-card__value">{{ formatUsd(finance.totalBalanceDue) }}</strong>
      </div>
      <div class="metric-card">
        <span class="metric-card__label">{{ $t('projectPage.profit') }}</span>
        <strong class="metric-card__value">{{ formatUsd(finance.profit) }}</strong>
      </div>
    </div>

    <el-row :gutter="16" class="finance-panels">
      <el-col :md="12" :xs="24">
        <el-card shadow="never">
          <div slot="header">{{ $t('reportPage.pipeline') }}</div>
          <div class="mini-grid">
            <div class="mini-item">
              <span>{{ $t('nav.leads') }}</span>
              <strong>{{ pipeline.leadCount || 0 }}</strong>
            </div>
            <div class="mini-item">
              <span>{{ $t('nav.quotes') }}</span>
              <strong>{{ pipeline.quoteCount || 0 }}</strong>
            </div>
            <div class="mini-item">
              <span>{{ $t('nav.contracts') }}</span>
              <strong>{{ pipeline.contractCount || 0 }}</strong>
            </div>
            <div class="mini-item">
              <span>{{ $t('nav.projects') }}</span>
              <strong>{{ pipeline.projectCount || 0 }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :md="12" :xs="24">
        <el-card shadow="never">
          <div slot="header">{{ $t('reportPage.finance') }}</div>
          <div class="mini-grid">
            <div class="mini-item">
              <span>{{ $t('financeCenterPage.operatingExpenseHint') }}</span>
              <strong>{{ formatUsd(finance.totalExpense) }}</strong>
            </div>
            <div class="mini-item">
              <span>{{ $t('financeCenterPage.totalLeadCostHint') }}</span>
              <strong>{{ formatUsd(finance.totalLeadCost) }}</strong>
            </div>
            <div class="mini-item">
              <span>{{ $t('projectPage.pmCommission') }}</span>
              <strong>{{ formatUsd(finance.totalPmCommission) }}</strong>
            </div>
            <div class="mini-item">
              <span>{{ $t('projectPage.profitMargin') }}</span>
              <strong>{{ finance.profitMargin == null ? '—' : finance.profitMargin + '%' }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" style="margin-top: 16px;">
      <div slot="header">{{ $t('reportPage.latestProjects') }}</div>
      <div class="latest-projects-table-wrap">
        <el-table :data="latestProjects" size="small" border class="latest-projects-table">
          <el-table-column prop="projectCode" :label="$t('grid.code')" width="120" fixed />
          <el-table-column prop="projectName" :label="$t('grid.project')" min-width="160" fixed />
          <el-table-column :label="$t('financeCenterPage.colContractTotal')" min-width="132" align="right" show-overflow-tooltip>
            <template slot-scope="scope">{{ formatUsd(scope.row.financeTotalContract) }}</template>
          </el-table-column>
          <el-table-column :label="$t('projectPage.totalReceived')" min-width="108" align="right" show-overflow-tooltip>
            <template slot-scope="scope">{{ formatUsd(scope.row.financeReceived) }}</template>
          </el-table-column>
          <el-table-column :label="$t('financeCenterPage.colTotalExpense')" min-width="100" align="right" show-overflow-tooltip>
            <template slot-scope="scope">{{ formatUsd(scope.row.financeExpense) }}</template>
          </el-table-column>
          <el-table-column :label="$t('projectPage.leadCost')" min-width="100" align="right" show-overflow-tooltip>
            <template slot-scope="scope">{{ formatUsd(scope.row.resolvedLeadCost) }}</template>
          </el-table-column>
          <el-table-column :label="$t('projectPage.profit')" min-width="92" align="right" show-overflow-tooltip>
            <template slot-scope="scope">{{ formatUsd(scope.row.financeProfit) }}</template>
          </el-table-column>
          <el-table-column :label="$t('projectPage.pmCommission')" min-width="118" align="right" show-overflow-tooltip>
            <template slot-scope="scope">{{ formatUsd(scope.row.financePmCommission) }}</template>
          </el-table-column>
          <el-table-column :label="$t('common.actions')" width="96" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="goProjectDetail(scope.row.id)">{{ $t('grid.viewProject') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import { reportOverview } from '@/api/report'
import { formatUsd } from '@/utils/formatUsd'
import { projectDetailLocation } from '@/utils/contractDetailNav'

export default {
  name: 'FinanceCenter',
  components: { ListPageShell },
  data() {
    return {
      range: [],
      pipeline: {},
      finance: {},
      latestProjects: []
    }
  },
  created() {
    this.setPreset('month')
  },
  methods: {
    formatUsd,
    setPreset(which) {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      if (which === 'week') {
        const day = now.getDay()
        const start = new Date(now)
        start.setDate(now.getDate() - day)
        const end = new Date(start)
        end.setDate(start.getDate() + 6)
        this.range = [fmt(start), fmt(end)]
      } else if (which === 'year') {
        this.range = [fmt(new Date(now.getFullYear(), 0, 1)), fmt(new Date(now.getFullYear(), 11, 31))]
      } else {
        this.range = [fmt(new Date(now.getFullYear(), now.getMonth(), 1)), fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0))]
      }
      this.load()
    },
    params() {
      return this.range && this.range.length === 2
        ? { startDate: this.range[0], endDate: this.range[1] }
        : {}
    },
    async load() {
      const resp = await reportOverview(this.params())
      const data = resp.data || resp
      this.pipeline = data.pipeline || {}
      this.finance = data.finance || {}
      this.latestProjects = data.latestProjects || []
    },
    goProjectDetail(id) {
      this.$router.push(projectDetailLocation(id, this.$route.fullPath))
    }
  }
}
</script>

<style scoped>
.finance-overview {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.metric-card {
  padding: 16px 18px;
  border-radius: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-card);
}

.metric-card__label {
  display: block;
  color: var(--color-text-sub);
  font-size: 12px;
  margin-bottom: 8px;
}

.metric-card__value {
  color: var(--color-text);
  font-size: 22px;
}

.finance-panels {
  margin-top: 0;
}

.mini-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.mini-item {
  padding: 12px;
  border-radius: 10px;
  background: var(--color-surface-soft);
  border: 1px solid var(--color-border-soft);
}

.mini-item span {
  display: block;
  color: var(--color-text-sub);
  font-size: 12px;
  margin-bottom: 6px;
}

.mini-item strong {
  color: var(--color-text);
  font-size: 18px;
}

@media (max-width: 900px) {
  .finance-overview {
    grid-template-columns: repeat(2, 1fr);
  }
}

.latest-projects-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.latest-projects-table {
  min-width: 1120px;
}

/* 财务中心表格：取消全局大写表头，英文/中文都更易读 */
.latest-projects-table ::v-deep .el-table__header-wrapper th.el-table__cell,
.latest-projects-table ::v-deep .el-table__fixed-header-wrapper th.el-table__cell {
  text-transform: none !important;
  letter-spacing: 0.01em !important;
  line-height: 1.35 !important;
}
</style>
